import { isEmpty } from 'ramda';
import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body, Label } from '~/components/atom/Typography';
import AnimatedCheckbox from '~/components/molecule/AnimatedCheckbox';
import DebouncedInput from '~/components/molecule/DebouncedInput';
import getKenmerkElementId from '~/components/page/Apps/Realworks/util/getKenmerkElementId';
import { RealworksRelatieSyncType } from '~/graphql/types';
import { isValid } from '~/util/Validation/Tag';
import TEST_ID from './index.testid';

const text = {
  tagFieldPlaceholder: 'Alleen kleine letters, cijfers en (-) zijn toegestaan',
  errorLabel: 'Vul een geldige tag in',
  nieuw: 'Nieuw',
};

type Attribute = {
  id: number;
  description: string | null;
  tag: string | null;
  checked: boolean;
};

export type Props = {
  row: Attribute;
  dataTestId?: string;
  className?: string;
  selectable: boolean;
  syncType: RealworksRelatieSyncType;
  isHighlighted?: boolean;
  highlightSubstring?: string;
  /** Whether this kenmerk is not yet present in the saved kenmerkMapping */
  isMissing?: boolean;
  onChange: (value: string | boolean | null, field: string) => void;
};

const KenmerkRow: React.FCC<Props> = ({
  dataTestId,
  row: { id, description, tag, checked },
  onChange,
  syncType,
  selectable,
  isHighlighted = false,
  highlightSubstring,
  isMissing = false,
}) => {
  const [hasError, setHasError] = useState(false);

  const onTagChange = value => {
    setHasError(!isEmpty(value) && !isValid(value));
    onChange(value, 'tag');
  };

  const onCheckboxChange = useCallback(
    () => onChange(!checked, 'checked'),
    [checked, onChange],
  );

  const externalErrors = hasError ? [text.errorLabel] : undefined;
  const hideInput =
    syncType === RealworksRelatieSyncType.FullExcludingKenmerken && checked;

  const appearance = (() => {
    switch (syncType) {
      case RealworksRelatieSyncType.FullExcludingKenmerken:
        return 'danger';
      case RealworksRelatieSyncType.LimitToKenmerken:
        return 'success';
      default:
        return undefined;
    }
  })();

  return (
    <React.Fragment>
      <HighlightedContainer
        direction="column"
        gap="xxxs"
        width="100%"
        justification="center"
        dataTestId={dataTestId}
        data-objectid={id}
        id={getKenmerkElementId(id)}
        $isHighlighted={isHighlighted}
      >
        <AnimatedCheckbox
          label={
            <JustificationContainer align="center" gap="xxs">
              <Label
                inline
                fontWeight="semiBold"
                size="base"
                highlight={highlightSubstring}
                margin={[null]}
              >
                {description ?? ''}
              </Label>
              {isMissing && (
                <NieuwBadge data-testid={TEST_ID.NIEUW_BADGE}>
                  <Body
                    size="s"
                    fontWeight="semiBold"
                    margin={[null]}
                    color={{ group: 'accent', variant: 'dark' }}
                  >
                    {text.nieuw}
                  </Body>
                </NieuwBadge>
              )}
            </JustificationContainer>
          }
          value={checked}
          selectable={selectable}
          onChange={() => {
            if (!selectable) return;
            onCheckboxChange();
          }}
          name="row-checkbox"
          icon={
            syncType === RealworksRelatieSyncType.FullExcludingKenmerken
              ? 'cross'
              : undefined
          }
          appearance={appearance}
        />
      </HighlightedContainer>

      <HighlightedContainer
        direction="column"
        gap="xxxs"
        width="100%"
        justification="center"
        $isHighlighted={isHighlighted}
      >
        {!hideInput && (
          <DebouncedInput
            value={tag ?? ''}
            onDebouncedChange={onTagChange}
            placeholder={text.tagFieldPlaceholder}
            externalErrors={externalErrors}
            name="row-input-field"
            width="100%"
          />
        )}
      </HighlightedContainer>
    </React.Fragment>
  );
};

const HighlightedContainer = styled(JustificationContainer)<{
  $isHighlighted: boolean;
}>(
  ({ theme, $isHighlighted }) => css`
    padding: ${theme.space('xxs')};
    border-radius: ${theme.getTokens().border.radius.base};
    transition: background-color 0.3s ease;

    ${$isHighlighted &&
    css`
      background-color: ${theme.color('accent', 'translucent')};
    `}
  `,
);

const NieuwBadge = styled.span(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    padding: ${theme.space('xxxs')} ${theme.space('xxs')};
    border-radius: ${theme.getTokens().border.radius.base};
    background-color: ${theme.color('accent', 'translucent')};
  `,
);

export default KenmerkRow;
